import React, { useState } from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { Heading, Text, PrimaryButton } from '@weave/design-system';
import { useSelector } from 'react-redux';
import { selectWeaveUserAcls } from '../../redux/actions/auth/auth.selectors';
import { CoreACLs } from '../../redux/actions/auth/auth.types';
import {
  API as AnnouncementAPI,
  GetAnnouncementsResponse,
} from '@weave/schema-gen-ts/dist/schemas/announcements/v1/announcements.pb';

import { useQuery } from 'react-query';
import { REACT_APP_API_URL } from 'config/app';
import { SimpleAnnouncementData } from './types';
import { useSimpleAnnouncementUpsertMutation } from './use-announcement-mutations';
import { SimpleAnnouncementDetail } from './announcement-detail';
import { SimpleAnnouncementForm } from './announcement-form';
import { SimpleAnnouncementAlertList } from './announcement-list';
import { authorizedFetch } from '../../helpers/utils';
import { getName } from './utils';

const baseUrl = REACT_APP_API_URL;
console.log('API Base URL:', baseUrl);
const container = css`
  width: 100vw;
  height: 90vh;
  padding: ${theme.spacing(4)};
  display: grid;
  gap: ${theme.spacing(4)};

  grid-template-rows: auto 1fr;
`;

export const AnnouncementConsole = () => {
  const { data: announcements } = useQuery({
    queryKey: ['announcements'],
    queryFn: async (): Promise<SimpleAnnouncementData[]> => {
      try {
        const response: GetAnnouncementsResponse = await AnnouncementAPI.GetAnnouncements(
          authorizedFetch(),
          {}
        );
        console.log('Fetched announcements:', response.announcements);

        // In a real implementation, fetch from an API
        return (
          response.announcements?.map((ann) => {
            if (!ann.payload) {
              console.warn(
                'Announcement payload is missing for announcement ID:',
                ann.id
              );
              return {
                id: ann.id ?? '',
                isPublished: ann.isPublished ?? false,
                title: 'No Title',
                message: 'No Message',
                path: '',
                createdBy: ann.createdByUser
                  ? getName(ann.createdByUser)
                  : 'Unknown User',
                createdOn: '',
                type: 'info',
                priority: 0,
                history: [],
              };
            }

            return {
              id: ann.id ?? '',
              isPublished: ann.isPublished ?? false,
              title: ann.name ?? '',
              message: ann.payload.message,
              path: ann.payload.path,
              createdBy: ann.createdByUser ? getName(ann.createdByUser) : 'Unknown User',
              createdOn: ann.createdAt ?? '',
              priority: ann.payload.priority,
              action: ann.payload.action,
              history: ann.publishHistory ?? [],
              type: ann.payload.type,
            };
          }) ?? []
        );
      } catch (error) {
        console.error('Error fetching announcements:', error);
        return [];
      }
    },
  });

  const { mutate: upsertAnnouncement } = useSimpleAnnouncementUpsertMutation();

  const hasACL = useSelector(selectWeaveUserAcls).indexOf(CoreACLs.OUTAGEREPORT) >= 0;
  const [selectedAnnouncementId, setSelectedAnnouncementId] = useState<string>(
    announcements?.[0]?.id || ''
  );
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  if (!hasACL) {
    return (
      <div style={{ textAlign: 'center', margin: 'auto' }}>
        <Heading>Access Denied!</Heading>
      </div>
    );
  }

  return (
    <div css={container}>
      <div
        style={{
          display: 'grid',
          gridTemplateAreas: '"header controls" "description description"',
          gridTemplateColumns: '1fr max-content',
        }}
      >
        <Heading css={{ gridArea: 'header' }} level={2}>
          Announcement Console
        </Heading>
        <Text css={{ gridArea: 'description', margin: 0 }}>
          Manage system announcements and outage alerts
        </Text>

        <div style={{ gridArea: 'controls' }}>
          <PrimaryButton onClick={() => setIsCreatingNew(true)}>
            Create New Announcement
          </PrimaryButton>
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gap: theme.spacing(2),
          gridTemplateColumns: '500px 1fr',
          gridTemplateRows: '1fr',
          overflowY: 'auto',
        }}
      >
        <SimpleAnnouncementAlertList
          announcements={announcements}
          selectedAnnouncementId={selectedAnnouncementId}
          onAnnouncementSelect={setSelectedAnnouncementId}
        />
        {isCreatingNew ? (
          <SimpleAnnouncementForm
            mode="create"
            onCancel={() => setIsCreatingNew(false)}
            onSave={({ announcement }) => {
              console.log('Creating new announcement:', { announcement });
              // In a real implementation, you would add to the data source here
              setIsCreatingNew(false);
              upsertAnnouncement(
                {
                  data: announcement,
                  mode: 'create',
                },
                {
                  onSuccess(data, variables, context) {
                    if (data.announcement?.id) {
                      setSelectedAnnouncementId(data.announcement.id);
                    }
                  },
                }
              );
            }}
          />
        ) : (
          <AnnouncementDetailWrapper
            key={selectedAnnouncementId}
            announcement={announcements?.find((ann) => ann.id === selectedAnnouncementId)}
          />
        )}
      </div>
    </div>
  );
};

const AnnouncementDetailWrapper = ({
  announcement,
}: {
  announcement?: SimpleAnnouncementData;
}) => {
  if (!announcement) {
    return <div>No announcement selected</div>;
  }

  return <SimpleAnnouncementDetail announcement={announcement} />;
};
